import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ScormAssign.css';
import { showToast } from './Toast';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { sortScorms, scormOptionLabel } from '../utils/scormHelpers';

const ScormUpload = () => {
  const [mode, setMode] = useState('new'); // new | update
  const [scorms, setScorms] = useState([]);
  const [scormsLoading, setScormsLoading] = useState(false);
  const [scormSort, setScormSort] = useState('alpha');
  const [selectedScormId, setSelectedScormId] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [updateNote, setUpdateNote] = useState('');
  const [rollbackNote, setRollbackNote] = useState('');
  const [customTitle, setCustomTitle] = useState('');

  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [serverStage, setServerStage] = useState('');
  const [serverPercent, setServerPercent] = useState(0);
  const [serverDetail, setServerDetail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | ready | uploading | success | error
  const fileInputRef = useRef(null);
  const uploadXhrRef = useRef(null);
  const successResetTimeoutRef = useRef(null);
  const uploadJobIdRef = useRef(null);
  const pollTimerRef = useRef(null);
  const terminalJobNotifiedRef = useRef(null);

  const clearPickedFile = () => {
    setFile(null);
    setProgress(0);
    try {
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {}
  };

  const resetForm = () => {
    try {
      if (successResetTimeoutRef.current) {
        clearTimeout(successResetTimeoutRef.current);
        successResetTimeoutRef.current = null;
      }
    } catch (e) {}
    try {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    } catch (e) {}
    uploadJobIdRef.current = null;
    setServerStage('');
    setServerPercent(0);
    setServerDetail('');
    clearPickedFile();
    setMessage('');
    setStatus('idle');
  };

  const API_BASE = process.env.REACT_APP_API_BASE || '';

  const loadScorms = async () => {
    setScormsLoading(true);
    try {
      const token = sessionStorage.getItem('accessToken');
      const res = await fetch(`${API_BASE}/admin/scorm/list`, {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || 'Error cargando SCORMs');
      setScorms(Array.isArray(data.scorms) ? data.scorms : []);
    } catch (e) {
      setScorms([]);
    } finally {
      setScormsLoading(false);
    }
  };

  useEffect(() => {
    loadScorms();
    const onUploaded = () => loadScorms();
    try { window.addEventListener('scorm:uploaded', onUploaded); } catch (e) {}
    return () => {
      try { window.removeEventListener('scorm:uploaded', onUploaded); } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedScorm = useMemo(() => {
    return (scorms || []).find(s => String(s.id) === String(selectedScormId)) || null;
  }, [scorms, selectedScormId]);

  const lastChangeText = useMemo(() => {
    if (!selectedScorm || !Array.isArray(selectedScorm.versions) || selectedScorm.versions.length === 0) return '';
    const latest = selectedScorm.versions
      .slice()
      .sort((a, b) => Number((b && b.version) || 0) - Number((a && a.version) || 0))[0];
    if (!latest) return '';
    const note = latest.note ? String(latest.note).trim() : '';
    if (latest.rolledBackFrom != null) {
      return `Rollback desde v${latest.rolledBackFrom}${note ? ` — ${note}` : ''}`;
    }
    const isFirstVersion = Number(latest.version) === 1;
    if (isFirstVersion) {
      return note ? `Primera versión — ${note}` : 'Primera versión';
    }
    return note ? `Actualización — ${note}` : 'Actualización';
  }, [selectedScorm]);

  const rollbackToVersion = async (targetVersion) => {
    if (!selectedScormId) return;
    const v = Number(targetVersion);
    if (!Number.isFinite(v) || v <= 0) return;

    const nextV = Number((selectedScorm && selectedScorm.version) || 1) + 1;
    const r = await Swal.fire({ title: 'Restaurar versión', text: `¿Restaurar el curso a la versión v${v}? Esto creará una nueva versión (v${nextV}) basada en esa versión anterior.`, icon: 'question', showCancelButton: true, confirmButtonText: 'Sí, restaurar', cancelButtonText: 'Cancelar' });
    if (!r.isConfirmed) return;

    const token = sessionStorage.getItem('accessToken');
    const loadingToast = showToast.loading(`Aplicando rollback a v${v}...`);
    try {
      const res = await fetch(`${API_BASE}/admin/scorm/rollback`, {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ scormId: selectedScormId, targetVersion: v, note: rollbackNote })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || 'Error aplicando rollback');

      showToast.success(data.message || `Rollback aplicado desde v${v}`);
      setMessage(data.message || `Rollback aplicado desde v${v}`);
      setRollbackNote('');

      // Refrescar lista y mantener selección
      await loadScorms();
      try { window.dispatchEvent(new CustomEvent('scorm:uploaded', { detail: data.scorm })); } catch(e) {}
      setShowHistoryModal(false);
    } catch (e) {
      showToast.error(e.message || 'Error aplicando rollback');
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const validateAndSetFile = (picked) => {
    setMessage('');
    setProgress(0);
    setStatus('idle');

    if (!picked) { setFile(null); return; }

    const name = String(picked.name || '').toLowerCase();
    if (!name.endsWith('.zip')) {
      setFile(null);
      setStatus('error');
      setMessage('Seleccione un archivo .zip');
      try { if (fileInputRef.current) fileInputRef.current.value = ''; } catch (err) {}
      return;
    }

    const maxBytes = 300 * 1024 * 1024;
    if (picked.size > maxBytes) {
      setFile(null);
      setStatus('error');
      setMessage('El archivo supera el límite de 300MB');
      try { if (fileInputRef.current) fileInputRef.current.value = ''; } catch (err) {}
      return;
    }

    setFile(picked);
    setStatus('ready');
  };

  const handleFile = (e) => {
    const picked = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    validateAndSetFile(picked);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (loading) return;
    const picked = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0] ? e.dataTransfer.files[0] : null;
    validateAndSetFile(picked);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (!loading) setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };

  const handleCancel = () => {
    try {
      if (uploadXhrRef.current) uploadXhrRef.current.abort();
    } catch (e) {}

    // Marcar el job como cancelado para romper cualquier espera asíncrona.
    try { uploadJobIdRef.current = `canceled-${Date.now()}`; } catch (e) {}

    try {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    } catch (e) {}
  };

  const makeJobId = () => {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (e) {}
    return `job-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const startPollingJob = (jobId) => {
    try {
      if (!jobId) return;
      uploadJobIdRef.current = jobId;
      terminalJobNotifiedRef.current = null;
      setServerStage('');
      setServerPercent(0);
      setServerDetail('');

      const token = sessionStorage.getItem('accessToken');
      const pollOnce = async () => {
        try {
          const res = await fetch(`${API_BASE}/admin/scorm/upload-status/${encodeURIComponent(jobId)}`,
            { method: 'GET', headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
          );
          const data = await res.json().catch(() => ({}));
          const job = data && data.job ? data.job : null;
          if (!job) return;
          if (job.stage != null) setServerStage(String(job.stage));
          if (job.serverPercent != null) setServerPercent(Number(job.serverPercent) || 0);
          if (job.detail != null) setServerDetail(String(job.detail));
          if (job.status === 'done' || job.status === 'error') {
            // Refrescar UI al completar para que deje de verse “(Procesando) ...”
            try {
              if (terminalJobNotifiedRef.current !== String(jobId)) {
                terminalJobNotifiedRef.current = String(jobId);
                // Mejor esfuerzo: recargar lista en este componente y notificar globalmente.
                try { loadScorms(); } catch (e) {}
                try {
                  const detail = job && job.result ? job.result : { jobId: String(jobId), status: job.status };
                  window.dispatchEvent(new CustomEvent('scorm:uploaded', { detail }));
                } catch (e) {}
              }
            } catch (e) {}

            try {
              if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
              }
            } catch (e) {}
          }
        } catch (e) {
          // ignore (puede ser 404 mientras el server registra el job)
        }
      };

      // Ejecutar inmediatamente y luego cada ~1s
      pollOnce();
      try {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      } catch (e) {}
      pollTimerRef.current = setInterval(pollOnce, 1000);
    } catch (e) {}
  };

  const waitForJobCompletion = async (jobId, timeoutMs = 30 * 60 * 1000) => {
    const id = String(jobId || '').trim();
    if (!id) return null;
    const token = sessionStorage.getItem('accessToken');
    const started = Date.now();

    // Loop simple; el polling visual (setInterval) ya corre en paralelo.
    while (Date.now() - started < timeoutMs) {
      // Si el usuario canceló o cambió el job, abortamos la espera
      if (uploadJobIdRef.current && String(uploadJobIdRef.current) !== id) {
        throw new Error('Subida cancelada');
      }

      try {
        const res = await fetch(`${API_BASE}/admin/scorm/upload-status/${encodeURIComponent(id)}`,
          { method: 'GET', headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
        );
        const data = await res.json().catch(() => ({}));
        const job = data && data.job ? data.job : null;
        if (job && (job.status === 'done' || job.status === 'error')) return job;
      } catch (e) {
        // ignore
      }

      await new Promise(r => setTimeout(r, 1000));
    }

    throw new Error('Tiempo de espera agotado procesando SCORM');
  };

  const handleUpload = async () => {
    if (!file) {
      setStatus('error');
      return setMessage('Seleccione un archivo .zip');
    }
    if (mode === 'update' && !selectedScormId) {
      setStatus('error');
      return setMessage('Seleccione el curso a actualizar');
    }
    if (loading) return;
    setLoading(true);
    setMessage('');
    setProgress(0);
    setServerStage('');
    setServerPercent(0);
    setServerDetail('');
    setStatus('uploading');

    try {
      const token = sessionStorage.getItem('accessToken');

      // ==================== OPCIÓN A: upload directo a S3 ====================
      // Solo para mode=upload por ahora. Si el endpoint no existe, hacemos fallback al flujo actual.
      const directS3Enabled = mode !== 'update';
      let data = null;
      let jobId = makeJobId();
      const loadingToast = showToast.loading(mode === 'update' ? 'Actualizando SCORM...' : 'Subiendo SCORM...');

      if (directS3Enabled) {
        // 1) Pedir presigned URL
        let initResp = null;
        try {
          const res = await fetch(`${API_BASE}/admin/scorm/upload-init`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              jobId,
              fileName: file && file.name ? String(file.name) : 'package.zip',
              contentType: file && file.type ? String(file.type) : 'application/zip',
              size: file && file.size ? Number(file.size) : null,
              customTitle: customTitle && String(customTitle).trim() ? String(customTitle).trim() : '',
            })
          });
          initResp = await res.json().catch(() => ({}));
          if (!res.ok) {
            // Si es un error de límite (403), lanzar error inmediato
            if (res.status === 403 && initResp.error) {
              throw new Error(initResp.error);
            }
            // si el backend no soporta todavía, caeremos al flujo legacy
            initResp = null;
          }
        } catch (e) {
          initResp = null;
        }

        if (initResp && initResp.uploadUrl && initResp.jobId) {
          // Validación defensiva: evitar PUT a una URL genérica/incompleta.
          // Un presigned URL típico incluye X-Amz-* (v4) y referencia el key.
          try {
            const u = String(initResp.uploadUrl || '');
            const k = initResp.uploadKey ? String(initResp.uploadKey) : '';
            const looksSigned = u.includes('X-Amz-') || u.includes('AWSAccessKeyId=');
            const referencesKey = k ? (u.includes(k) || u.includes(encodeURIComponent(k))) : true;
            if (!looksSigned || !referencesKey) {
              throw new Error('Presigned URL inválido (revisa CORS/vars de S3 en backend)');
            }
          } catch (e) {
            throw e;
          }

          jobId = String(initResp.jobId);
          startPollingJob(jobId);

          // 2) Subir el ZIP directo a S3
          data = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            uploadXhrRef.current = xhr;
            xhr.open('PUT', String(initResp.uploadUrl));
            // Content-Type debe coincidir con el firmado (si aplica)
            try {
              const ct = file && file.type ? String(file.type) : 'application/zip';
              xhr.setRequestHeader('Content-Type', ct);
            } catch (e) {}

            xhr.upload.onprogress = (evt) => {
              if (!evt) return;
              if (evt.lengthComputable) {
                const pct = Math.max(0, Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
                setProgress(pct);
              }
            };

            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) return resolve({ accepted: true, jobId });
              return reject(new Error(`Error subiendo a S3 (${xhr.status})`));
            };
            xhr.onerror = () => reject(new Error('Error de red subiendo a S3'));
            xhr.onabort = () => reject(new Error('Subida cancelada'));
            xhr.send(file);
          });
        }
      }

      // ==================== FALLBACK: flujo legacy (subida al backend) ====================
      if (!data) {
        const form = new FormData();
        form.append('jobId', jobId);
        form.append('file', file);
        if (customTitle && String(customTitle).trim()) form.append('customTitle', String(customTitle).trim());
        if (mode === 'update') {
          form.append('scormId', selectedScormId);
          if (updateNote && String(updateNote).trim()) form.append('note', String(updateNote).trim());
        }

        data = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          uploadXhrRef.current = xhr;

          const url = mode === 'update' ? `${API_BASE}/admin/scorm/update` : `${API_BASE}/admin/scorm/upload`;
          xhr.open('POST', url);
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

          xhr.upload.onprogress = (evt) => {
            if (!evt) return;
            if (evt.lengthComputable) {
              const pct = Math.max(0, Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
              setProgress(pct);
              if (pct >= 100 && !pollTimerRef.current) {
                startPollingJob(jobId);
              }
            }
          };

          xhr.onload = () => {
            let body = {};
            try { body = JSON.parse(xhr.responseText || '{}'); } catch (e) {}
            if (xhr.status >= 200 && xhr.status < 300) return resolve(body);
            return reject(new Error(body.error || body.message || `Error subiendo SCORM (${xhr.status})`));
          };

          xhr.onerror = () => reject(new Error('Error de red subiendo SCORM'));
          xhr.onabort = () => reject(new Error('Subida cancelada'));

          setTimeout(() => startPollingJob(jobId), 800);
          xhr.send(form);
        });
      }

      // Si el backend respondió 202 Accepted, el trabajo sigue en background.
      // Esperamos a que el job termine para reportar éxito/error real.
      let finalResultTitle = '';
      if (data && data.accepted && data.jobId) {
        const finalJob = await waitForJobCompletion(String(data.jobId));
        if (finalJob && finalJob.status === 'error') {
          throw new Error(finalJob.error || 'Error procesando SCORM');
        }
        finalResultTitle = finalJob && finalJob.result && finalJob.result.title
          ? String(finalJob.result.title)
          : '';
      }

      toast.dismiss(loadingToast);
      setProgress(100);
      setServerStage('Listo');
      setServerPercent(100);
      setServerDetail('');
      setStatus('success');
      const uploadedTitle = finalResultTitle || (data && data.scorm && data.scorm.title ? String(data.scorm.title) : '');
      const okMsg = uploadedTitle
        ? (mode === 'update' ? `SCORM actualizado: ${uploadedTitle}` : `SCORM subido: ${uploadedTitle}`)
        : (mode === 'update' ? 'SCORM actualizado correctamente' : 'SCORM subido correctamente');
      showToast.success(okMsg);
      setMessage(okMsg);
      setUpdateNote('');
      setCustomTitle('');

      // Emitir evento global para notificar subida
      try {
        const detail = (data && data.scorm) ? data.scorm : (data && data.jobId ? { jobId: String(data.jobId) } : null);
        window.dispatchEvent(new CustomEvent('scorm:uploaded', { detail }));
      } catch(e) {}

      // Refrescar lista local inmediatamente (y una vez más un poco después)
      try { await loadScorms(); } catch (e) {}
      try { setTimeout(() => { try { loadScorms(); } catch (e) {} }, 1500); } catch (e) {}

      // Limpiar campos (sin borrar el mensaje)
      clearPickedFile();

      // Dejar el formulario limpio automáticamente tras un breve feedback
      try {
        if (successResetTimeoutRef.current) clearTimeout(successResetTimeoutRef.current);
        successResetTimeoutRef.current = setTimeout(() => {
          setMessage('');
          setStatus('idle');
          successResetTimeoutRef.current = null;
        }, 2000);
      } catch (e) {}
    } catch (err) {
      setStatus(err && err.message === 'Subida cancelada' ? 'idle' : 'error');
      try {
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      } catch (e) {}
      const isLimit = err.message && err.message.toLowerCase().includes('límite');
      if (isLimit) {
        Swal.fire({ title: 'Límite alcanzado', text: err.message, icon: 'warning' });
      } else {
        showToast.error(err.message || 'Error subiendo SCORM');
      }
      setMessage(err.message || 'Error');
    } finally {
      uploadXhrRef.current = null;
      uploadJobIdRef.current = null;
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    const n = Number(bytes || 0);
    if (!n) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
    const val = n / Math.pow(1024, i);
    return `${val.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  };

  return (
  <div className="scorm-admin-card col-12 text-left">
    <div className="scorm-card">
      <div className="scorm-card__header">
        <div className="scorm-card__title">SCORM</div>
        <div className="scorm-card__subtitle">Archivo .zip • Máximo 300MB</div>
      </div>
      <div className="scorm-card__body">
        <div className="scorm-row">
          <div className="scorm-field">
            <label>Acción</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className={`scorm-btn ${mode === 'new' ? 'scorm-btn--primary' : 'scorm-btn--secondary'}`}
                onClick={() => {
                  setMode('new');
                  setSelectedScormId('');
                  setUpdateNote('');
                  setRollbackNote('');
                  setCustomTitle('');
                  setMessage('');
                  setStatus('idle');
                }}
                disabled={loading}
              >
                Nuevo curso
              </button>
              <button
                type="button"
                className={`scorm-btn ${mode === 'update' ? 'scorm-btn--primary' : 'scorm-btn--secondary'}`}
                onClick={() => {
                  setMode('update');
                  setUpdateNote('');
                  setRollbackNote('');
                  setCustomTitle('');
                  setMessage('');
                  setStatus('idle');
                }}
                disabled={loading}
              >
                Actualizar curso
              </button>
            </div>
          </div>
        </div>

        {mode === 'update' && (
          <div className="scorm-row">
            <div className="scorm-field">
              <label>Curso a actualizar</label>
              <div className="scorm-sort-toggle">
                <button type="button" className={`scorm-sort-btn ${scormSort === 'alpha' ? 'scorm-sort-btn--active' : ''}`} onClick={() => setScormSort('alpha')}>A-Z</button>
                <button type="button" className={`scorm-sort-btn ${scormSort === 'recent' ? 'scorm-sort-btn--active' : ''}`} onClick={() => setScormSort('recent')}>Recientes</button>
              </div>
              <select
                className="scorm-input"
                value={selectedScormId}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedScormId(id);
                  setUpdateNote('');
                  setRollbackNote('');
                  // Auto-fill título del curso seleccionado
                  const found = (scorms || []).find(s => String(s.id) === String(id));
                  setCustomTitle(found && found.title ? String(found.title) : '');
                }}
                disabled={loading || scormsLoading}
              >
                <option value="">-- Seleccione un curso --</option>
                {sortScorms(scorms, scormSort).map((s) => {
                  const v = s && s.version ? `v${s.version}` : 'v1';
                  return (
                    <option key={s.id} value={s.id}>
                      {scormOptionLabel(s)} ({v})
                    </option>
                  );
                })}
              </select>

              {selectedScorm && (
                <div className="scorm-meta" style={{ marginTop: 6 }}>
                  <div><strong>Versión actual:</strong> {selectedScorm.version ? `v${selectedScorm.version}` : 'v1'}</div>
                  {selectedScorm.updatedAt && (
                    <div style={{ opacity: 0.85 }}><strong>Actualizado:</strong> {String(selectedScorm.updatedAt)}</div>
                  )}
                  {lastChangeText && (
                    <div style={{ opacity: 0.9 }}><strong>Último cambio:</strong> {lastChangeText}</div>
                  )}
                  {Array.isArray(selectedScorm.versions) && selectedScorm.versions.length > 0 && (
                    <div style={{ opacity: 0.85 }}>
                      <strong>Historial:</strong> {selectedScorm.versions
                        .slice()
                        .sort((a, b) => Number((b && b.version) || 0) - Number((a && a.version) || 0))
                        .slice(0, 5)
                        .map(v => `v${v.version}`)
                        .join(', ')}
                      {selectedScorm.versions.length > 5 ? '…' : ''}
                    </div>
                  )}

                  <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="scorm-btn scorm-btn--secondary"
                      onClick={() => setShowHistoryModal(true)}
                      disabled={loading}
                    >
                      Ver historial
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {mode === 'update' && (
          <div className="scorm-row">
            <div className="scorm-field">
              <label>Nota / motivo (opcional)</label>
              <input
                type="text"
                className="scorm-input"
                value={updateNote}
                onChange={(e) => setUpdateNote(e.target.value)}
                disabled={loading}
                placeholder="Ej: Se actualizó contenido / corrección de evaluación"
              />
              <div className="scorm-meta">Se guarda en el historial como parte de la actualización.</div>
            </div>
          </div>
        )}

        <div className="scorm-row">
          <div className="scorm-field">
            <label>Título personalizado (opcional)</label>
            <input
              type="text"
              className="scorm-input"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              disabled={loading}
              placeholder="Dejar vacío para usar el título del manifest"
            />
            <div className="scorm-meta">Si no se especifica, se usará el título del archivo imsmanifest.xml</div>
          </div>
        </div>

        {showHistoryModal && selectedScorm && (
          <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', overflowY: 'auto' }}>
            <div className="modal-dialog modal-lg" style={{ marginTop: '8vh' }}>
              <div className="modal-content" style={{ borderRadius: '12px' }}>
                <div className="modal-header" style={{
                  background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)',
                  color: 'white',
                  borderRadius: '12px 12px 0 0'
                }}>
                  <h5 className="modal-title" style={{ margin: 0 }}>
                    Historial de versiones
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowHistoryModal(false)}></button>
                </div>

                <div className="modal-body" style={{ background: '#fff' }}>
                  <div className="mb-2" style={{ opacity: 0.9 }}>
                    <div><strong>Curso:</strong> {selectedScorm.title || selectedScorm.id}</div>
                    <div><strong>Versión activa:</strong> {selectedScorm.version ? `v${selectedScorm.version}` : 'v1'}</div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label"><strong>Motivo (opcional)</strong></label>
                    <input
                      className="form-control"
                      type="text"
                      value={rollbackNote}
                      onChange={(e) => setRollbackNote(e.target.value)}
                      placeholder="Ej: Volver a versión estable por bug"
                    />
                    <small className="text-muted">Se guarda en el historial del rollback.</small>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-sm table-striped">
                      <thead>
                        <tr>
                          <th style={{ width: 90 }}>Versión</th>
                          <th>Creada</th>
                          <th>Último cambio</th>
                          <th>Detalle</th>
                          <th style={{ width: 140 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(Array.isArray(selectedScorm.versions) ? selectedScorm.versions : [])
                          .slice()
                          .sort((a, b) => Number((b && b.version) || 0) - Number((a && a.version) || 0))
                          .map((ver) => {
                            const verNum = Number(ver && ver.version);
                            const isActive = Number(selectedScorm.version || 1) === verNum;
                            const isRollback = ver && ver.rolledBackFrom != null;
                            const note = ver && ver.note ? String(ver.note) : '';
                            const changeText = isRollback ? `Rollback desde v${ver.rolledBackFrom}` : (verNum === 1 ? 'Primera versión' : 'Actualización');
                            return (
                              <tr key={String(verNum)}>
                                <td><strong>{Number.isFinite(verNum) ? `v${verNum}` : '-'}</strong>{isActive ? ' (activa)' : ''}</td>
                                <td>{ver && ver.createdAt ? String(ver.createdAt) : '-'}</td>
                                <td>{changeText}</td>
                                <td>
                                  {note ? <div style={{ opacity: 0.85 }}>{note}</div> : '-'}
                                </td>
                                <td className="text-end">
                                  <button
                                    type="button"
                                    className="scorm-btn scorm-btn--primary"
                                    onClick={() => rollbackToVersion(verNum)}
                                    disabled={loading || isActive || !Number.isFinite(verNum)}
                                    title={isActive ? 'Ya es la versión activa' : 'Restaurar a esta versión (crea una nueva versión)'}
                                  >
                                    Restaurar
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>

                  <small className="text-muted">
                    Nota: “Restaurar” no cambia el número de la versión anterior; crea una nueva versión con el contenido restaurado.
                  </small>
                </div>

                <div className="modal-footer" style={{
                  padding: '15px 20px',
                  borderTop: '1px solid #eee',
                  borderRadius: '0 0 12px 12px',
                  background: '#f8f9fa'
                }}>
                  <button
                    type="button"
                    className="scorm-btn scorm-btn--secondary"
                    onClick={() => setShowHistoryModal(false)}
                    disabled={loading}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="scorm-row">
          <div className="scorm-field">
            <label>Selecciona o arrastra archivo</label>
            <div
              className={`scorm-dropzone${isDragging ? ' scorm-dropzone--active' : ''}${file ? ' scorm-dropzone--has-file' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => { if (!loading && fileInputRef.current) fileInputRef.current.click(); }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                onChange={handleFile}
                disabled={loading}
                style={{ display: 'none' }}
              />
              {file ? (
                <div className="scorm-dropzone__file">
                  <span>📦</span>
                  <strong>{file.name}</strong>
                  <span style={{ opacity: 0.7 }}>({formatBytes(file.size)})</span>
                </div>
              ) : (
                <div className="scorm-dropzone__placeholder">
                  <span style={{ fontSize: 24 }}>📁</span>
                  <span>Arrastra un .zip aquí o haz clic para seleccionar</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="scorm-actions">
          <button
            type="button"
            className="scorm-btn scorm-btn--primary"
            onClick={handleUpload}
            disabled={loading || !file || (mode === 'update' && !selectedScormId)}
          >
            {loading ? (mode === 'update' ? 'Actualizando...' : 'Subiendo...') : (mode === 'update' ? 'Actualizar' : 'Subir')}
          </button>
          <button
            type="button"
            className="scorm-btn scorm-btn--secondary"
            onClick={loading ? handleCancel : resetForm}
            disabled={loading ? false : (!file && !message)}
          >
            {loading ? 'Cancelar' : 'Limpiar'}
          </button>
        </div>

        {loading && (
          <div className="scorm-progress">
            <div className="scorm-progress__bar">
              <div className="scorm-progress__fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="scorm-progress__label">
              {progress < 100
                ? (progress ? `Subiendo ZIP al servidor: ${progress}%` : 'Preparando subida...')
                : `ZIP subido. Procesando en servidor${serverStage ? `: ${serverStage}` : '...'}`}
            </div>

            {progress >= 100 && (
              <>
                <div className="scorm-progress__bar" style={{ marginTop: 8 }}>
                  <div
                    className="scorm-progress__fill"
                    style={{ width: `${serverPercent}%`, background: '#6366f1' }}
                  />
                </div>
                <div className="scorm-progress__label" style={{ opacity: 0.9 }}>
                  {serverStage ? `Procesando: ${serverStage}` : 'Procesando...'}
                  {serverPercent ? ` • ${serverPercent}%` : ''}
                  {serverDetail ? ` • ${serverDetail}` : ''}
                </div>
              </>
            )}
          </div>
        )}

        {message && (
          <div className={`scorm-message ${status === 'error' ? 'scorm-message--error' : ''}`}>{message}</div>
        )}
      </div>
    </div>
  </div>
  );
};

export default ScormUpload;
